/**
 * Search Results Sorter
 * Fetches all search result pages, extracts product-blocks, and sorts them
 * according to product type and vendor priority.
 */
(function() {
  'use strict';

  // Search now regularly matches 800-1000+ products for a single term. Fetching every page
  // client-side to re-sort would mean dozens of pages, hundreds of DOM nodes to sort/render/
  // animate, and (if all fired at once) a flood of concurrent requests - all of which hang the
  // browser. Cap how much we pull, and fetch what we do pull in small batches rather than all
  // at once.
  const MAX_PAGES_TO_FETCH = 10;
  const FETCH_BATCH_SIZE = 4;

  class SearchResultsSorter {
    constructor() {
      this.productGrid = null;
      this.searchQuery = null;
      this.isRunning = false;
      this.init();
    }

    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.start();
          this.setupFilterListener();
        });
      } else {
        this.start();
        this.setupFilterListener();
      }
    }

    setupFilterListener() {
      // Watch for filter updates - when the collection-listing gets new content via AJAX
      const collectionListing = document.querySelector('.collection-listing[data-ajax-container]');
      if (!collectionListing) return;

      let lastUrl = window.location.href;

      const observer = new MutationObserver(() => {
        // Check if URL changed (filters were applied)
        const currentUrl = window.location.href;
        if (currentUrl !== lastUrl) {
          lastUrl = currentUrl;
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('q') && !this.isRunning) {
            // Wait a bit for the filter system to finish updating
            setTimeout(() => {
              this.productGrid = document.querySelector('.product-grid[data-search-sorter-enabled]');
              if (this.productGrid) {
                this.start();
              }
            }, 500);
          }
        }
      });

      // Watch for content changes in the collection listing
      observer.observe(collectionListing, {
        childList: true,
        subtree: true
      });

      // Also watch for ajax-loading class removal
      const classObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            if (target === collectionListing && !target.classList.contains('ajax-loading')) {
              const currentUrl = window.location.href;
              if (currentUrl !== lastUrl) {
                lastUrl = currentUrl;
                const urlParams = new URLSearchParams(window.location.search);
                if (urlParams.get('q') && !this.isRunning) {
                  setTimeout(() => {
                    this.productGrid = document.querySelector('.product-grid[data-search-sorter-enabled]');
                    if (this.productGrid) {
                      this.start();
                    }
                  }, 300);
                }
              }
            }
          }
        });
      });

      classObserver.observe(collectionListing, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    start() {
      // Check if we're on a search results page
      const urlParams = new URLSearchParams(window.location.search);
      this.searchQuery = urlParams.get('q');

      if (!this.searchQuery) {
        return; // Not a search page
      }

      this.productGrid = document.querySelector('.product-grid[data-search-sorter-enabled]');
      if (!this.productGrid) {
        return; // No product grid found
      }

      this.fetchAllSearchPages();
    }

    showLoading() {
      this.productGrid.innerHTML = '';
      const loadingDiv = document.createElement('div');
      loadingDiv.className = 'search-loading';
      loadingDiv.textContent = 'Loading search results...';
      loadingDiv.style.textAlign = 'center';
      loadingDiv.style.padding = '2rem';
      this.productGrid.appendChild(loadingDiv);
    }

    // The hidden data-initial-products container is scanned by animate-on-scroll.js on page
    // load (display:none doesn't hide it from querySelectorAll), which marks its elements
    // .cc-animate-init while they're stuck off-screen and never intersecting. Cloning those
    // nodes as-is carries that class over, so the MutationObserver-driven rescan (which only
    // picks up elements NOT already marked .cc-animate-init) skips them forever and they never
    // fade in. Strip the animation state so the clones are treated as fresh elements.
    resetAnimationState(block) {
      const animatedElems = block.hasAttribute('data-cc-animate')
        ? [block, ...block.querySelectorAll('[data-cc-animate]')]
        : Array.from(block.querySelectorAll('[data-cc-animate]'));
      animatedElems.forEach(el => el.classList.remove('cc-animate-init', 'cc-animate-in'));
      return block;
    }

    // Extract usable product-blocks (excluding Image Placeholder) from a product-grid element
    extractProductBlocks(gridEl) {
      const blocks = [];
      gridEl.querySelectorAll('.product-block[data-product-type]').forEach(block => {
        const productType = block.getAttribute('data-product-type');
        if (productType && productType !== 'Image Placeholder') {
          blocks.push(block);
        }
      });
      return blocks;
    }

    async fetchSearchPage(filterParams, pageNumber) {
      const baseUrl = window.location.origin + window.location.pathname;
      const url = new URL(baseUrl, window.location.origin);
      filterParams.forEach((value, key) => {
        url.searchParams.set(key, value);
      });
      if (pageNumber > 1) {
        url.searchParams.set('page', pageNumber);
      }
      url.searchParams.set('section_id', 'main-search');

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const grid = doc.querySelector('.product-grid[data-search-sorter-enabled]');
      if (!grid) {
        return { blocks: [] };
      }

      return { blocks: this.extractProductBlocks(grid).map(block => block.cloneNode(true)) };
    }

    // Fetch a list of page numbers in small concurrent batches rather than all at once, so we
    // don't open dozens of simultaneous requests when a search spans many pages.
    async fetchPagesInBatches(pageNumbers, fetchPageFn, batchSize) {
      const results = [];
      for (let i = 0; i < pageNumbers.length; i += batchSize) {
        const batch = pageNumbers.slice(i, i + batchSize);
        const batchResults = await Promise.all(batch.map(fetchPageFn));
        results.push(...batchResults);
      }
      return results;
    }

    async fetchAllSearchPages() {
      // Prevent concurrent runs
      if (this.isRunning) {
        return;
      }
      this.isRunning = true;

      // Reuse the products already rendered server-side for this page instead of
      // re-fetching page 1 over the network.
      const initialContainer = this.productGrid.querySelector('[data-initial-products]');
      const initialBlocks = initialContainer
        ? this.extractProductBlocks(initialContainer).map(block => this.resetAnimationState(block.cloneNode(true)))
        : [];

      const totalItems = parseInt(this.productGrid.getAttribute('data-total-items'), 10) || initialBlocks.length;
      const pageSize = parseInt(this.productGrid.getAttribute('data-page-size'), 10) || initialBlocks.length;
      const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1;
      const pagesToFetch = Math.min(totalPages, MAX_PAGES_TO_FETCH);

      if (totalPages > MAX_PAGES_TO_FETCH) {
        console.warn(`Search matched ${totalItems} items across ${totalPages} pages; only fetching the first ${MAX_PAGES_TO_FETCH} pages (${pagesToFetch * pageSize} items) to keep sorting responsive.`);
      }

      let allProductBlocks = initialBlocks;

      if (pagesToFetch > 1) {
        // Only hit the network (and show a loading state) when there is more than one page.
        this.showLoading();

        // Carry over all URL parameters (including filters) except page and section_id
        const currentUrlParams = new URLSearchParams(window.location.search);
        const filterParams = new URLSearchParams();
        currentUrlParams.forEach((value, key) => {
          if (key !== 'page' && key !== 'section_id') {
            filterParams.set(key, value);
          }
        });

        try {
          // Fetch the remaining pages in small batches instead of all at once.
          const remainingPageNumbers = Array.from({ length: pagesToFetch - 1 }, (_, i) => i + 2);
          const remainingResults = await this.fetchPagesInBatches(
            remainingPageNumbers,
            pageNumber => this.fetchSearchPage(filterParams, pageNumber),
            FETCH_BATCH_SIZE
          );
          remainingResults.forEach(result => {
            allProductBlocks = allProductBlocks.concat(result.blocks);
          });
        } catch (error) {
          console.error('Error fetching search pages:', error);
        }
      }

      // Sort product blocks
      if (allProductBlocks.length > 0) {
        this.sortAndDisplayProducts(allProductBlocks);
      } else {
        // No products found, show empty state
        this.productGrid.innerHTML = '';
        const emptyMessage = document.createElement('div');
        emptyMessage.className = 'align-centre fully-spaced-row';
        emptyMessage.textContent = 'No products found.';
        this.productGrid.appendChild(emptyMessage);
      }

      this.isRunning = false;
    }

    sortAndDisplayProducts(productBlocks) {
      // Wait for SearchSortUtils to be available
      const checkUtils = () => {
        if (window.SearchSortUtils) {
          // Sort products using shared utility
          const sortedBlocks = window.SearchSortUtils.sortProductBlocks(productBlocks, this.searchQuery);

          // Clean up sessionStorage
          sessionStorage.removeItem('search_source_url');

          // Clear loading state
          this.productGrid.innerHTML = '';

          // Append sorted products
          sortedBlocks.forEach(block => {
            this.productGrid.appendChild(block);
          });

          // Update result count if needed
          this.updateResultCount(sortedBlocks.length);
        } else {
          // Retry after a short delay
          setTimeout(checkUtils, 50);
        }
      };
      checkUtils();
    }

    updateResultCount(count) {
      const resultCountElement = document.querySelector('[data-search-result-count]');
      if (resultCountElement) {
        const text = resultCountElement.textContent;
        const match = text.match(/(\d+)/);
        if (match) {
          resultCountElement.textContent = text.replace(match[1], count);
        }
      }
    }
  }

  // Initialize when script loads
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new SearchResultsSorter();
    });
  } else {
    new SearchResultsSorter();
  }
})();

